import React, { useState, useMemo } from 'react';
import { X, Search, FileText, Calendar, Filter } from 'lucide-react';
import DateInput from '../common/DateInput';

// Transaction Type Constants
const LEAVE_TRANSACTION_TYPES = [
  { value: 'Accrual', label: 'Accrual' },
  { value: 'Request', label: 'Request' },
  { value: 'BalanceAdjustment', label: 'Balance Adjustment' },
  { value: 'Grant', label: 'Grant' },
  { value: 'CarryForwardAdjustment', label: 'Carry Forward Adjustment' },
  { value: 'AccrualSuspensionAdjustment', label: 'Accrual Suspension Adjustment' },
  { value: 'CarryForwardTransfer', label: 'Carry Forward Transfer' },
  { value: 'BalanceExpired', label: 'Balance Expired' },
  { value: 'LeaveOfAbsenceRequest', label: 'Leave Of Absence Request' },
  { value: 'LeaveBuyRequest', label: 'Leave Buy Request' },
  { value: 'LeaveSellRequest', label: 'Leave Sell Request' }
];

// Helper function to get transaction type label
const getTransactionTypeLabel = (value) => {
  const type = LEAVE_TRANSACTION_TYPES.find(t => t.value === value);
  return type ? type.label : value || 'N/A';
};

const LeaveTransactionModal = ({ isOpen, onClose, leaveTypeId, transactions, isLoading, onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const itemsPerPage = 15;

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setCurrentPage(1);
      setStartDate('');
      setEndDate('');
      setTransactionType('');
    }
  }, [isOpen]);

  const handleApplyFilter = () => {
    if (onFilterChange) {
      onFilterChange({
        startDate,
        endDate,
        transactionType
      });
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    if (onFilterChange) {
      onFilterChange({
        startDate: '',
        endDate: '',
        transactionType: ''
      });
    }
  };

  // Filter transactions based on search
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];
    if (!searchTerm) return transactions;

    const lowerSearch = searchTerm.toLowerCase();
    return transactions.filter(tx =>
      tx.TransactionType?.toLowerCase().includes(lowerSearch) ||
      tx.Comment?.toLowerCase().includes(lowerSearch) ||
      tx.ReasonCodeId?.toLowerCase().includes(lowerSearch)
    );
  }, [transactions, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTransactions, currentPage]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Leave Transaction Breakdown</h2>
              <p className="text-sm text-gray-600 mt-1">{leaveTypeId}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-4 text-gray-600 text-lg">Loading transactions...</p>
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                  </div>

                  {/* Date Range Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        From Date (Optional)
                      </label>
                      <DateInput
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        To Date (Optional)
                      </label>
                      <DateInput
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        <Filter className="w-3 h-3 inline mr-1" />
                        Transaction Type (Optional)
                      </label>
                      <select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">All Types</option>
                        {LEAVE_TRANSACTION_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      {startDate && endDate && transactionType
                        ? `Filtering: ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()} • ${getTransactionTypeLabel(transactionType)}`
                        : startDate && endDate
                        ? `Date Range: ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
                        : startDate
                        ? `From: ${new Date(startDate).toLocaleDateString()}`
                        : endDate
                        ? `To: ${new Date(endDate).toLocaleDateString()}`
                        : transactionType
                        ? `Type: ${getTransactionTypeLabel(transactionType)}`
                        : 'Select filters to narrow down results'}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleApplyFilter}
                        disabled={!startDate && !endDate && !transactionType}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                      <button
                        onClick={handleClearFilter}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors whitespace-nowrap"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by type, comment, or reason code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredTransactions.length} of {transactions.length} transactions
                  </p>
                </div>

                {/* Table or Empty State */}
                {transactions.length === 0 ? (
                  <div className="border border-gray-200 rounded-lg p-12 text-center bg-gray-50">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg font-medium mb-2">No transactions found</p>
                    <p className="text-gray-500 text-sm">Try adjusting your date range filter to see transactions</p>
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="border border-gray-200 rounded-lg p-12 text-center bg-gray-50">
                    <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg font-medium mb-2">No matching transactions</p>
                    <p className="text-gray-500 text-sm">Your search didn't match any transactions. Try different keywords.</p>
                  </div>
                ) : (
                  <>
                    {/* Table */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Transaction Type
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Comment
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                Reason Code
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedTransactions.map((tx, index) => (
                              <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                  {tx.TransactionDate ? new Date(tx.TransactionDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  }) : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    tx.TransactionType === 'Grant'
                                      ? 'bg-green-100 text-green-800'
                                      : tx.TransactionType === 'BalanceAdjustment'
                                      ? 'bg-purple-100 text-purple-800'
                                      : tx.TransactionType === 'Accrual'
                                      ? 'bg-blue-100 text-blue-800'
                                      : tx.TransactionType === 'Request'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {getTransactionTypeLabel(tx.TransactionType)}
                                  </span>
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold text-right ${
                                  tx.Amount > 0 ? 'text-green-600' : tx.Amount < 0 ? 'text-red-600' : 'text-gray-700'
                                }`}>
                                  {tx.Amount != null ? (tx.Amount > 0 ? '+' : '') + tx.Amount.toFixed(2) : 'N/A'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                  {tx.Comment || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                  {tx.ReasonCodeId || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6">
                        <div className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveTransactionModal;
